/**
 * Centralised dashboard date-range state.
 *
 * Every analytics widget (sparklines, chart, breakdown, donuts, geo,
 * cohorts, top products, etc.) subscribes to ONE source of truth so
 * changing the picker once propagates everywhere. The selection is
 * persisted to localStorage so reloads remember the user's last window.
 *
 * All dates are computed in the STORE'S reporting timezone, not the
 * browser's local timezone. This matters because:
 *   • a store's "Today" is the same regardless of where the
 *     operator is sitting,
 *   • the backend buckets rollups in that same zone,
 *   • a US-based operator opening the dashboard at 9 PM PT should still
 *     see the store's "Today" (which may already be the store's tomorrow).
 *
 * Shape:
 *   {
 *     preset: "today" | "yesterday" | "last7" | ... | "custom",
 *     from:   "YYYY-MM-DD"  (always set — even for fixed presets)
 *     to:     "YYYY-MM-DD"  (always set)
 *     label:  "Today" | "Last 7 days" | "Jan 1, 2023 – May 21, 2026"
 *   }
 *
 * For backend calls: every endpoint that takes a date filter accepts
 *   ?preset=<id>&from=<YYYY-MM-DD>&to=<YYYY-MM-DD>
 * and the backend's _resolve_preset_dates() honours either route.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { REPORT_TZ } from "../components/listiclebuilders/reportTz";

const STORAGE_KEY = "fos:dashboard:date-range";

// This app is single-store and pinned to one reporting timezone.
// We compute all "today"-relative anchors in that timezone so the FE and BE
// always agree on what "Today" means, even when an operator opens the
// dashboard from a browser in another timezone.
//
// ONE DEFINITION, re-exported under this area's older name. `reportTz.js` owns
// it and reads REACT_APP_REPORT_TIMEZONE, which MUST equal the backend's
// REPORT_TIMEZONE — see that file for what happens when they drift.
const SHOP_TZ = REPORT_TZ;

function fmtLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Returns today's date (year, month, day) in SHOP_TZ as a `new Date()`
 * whose local-tz components are the SHOP_TZ components. Use this in place
 * of `new Date()` whenever we need "today in the shop's timezone". */
function nowInShopTz() {
  // Format current instant in SHOP_TZ and parse the parts back into a
  // local Date so that all the date arithmetic below (which uses
  // getFullYear/getMonth/getDate) yields shop-tz aligned values.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: SHOP_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const pick = (t) => Number(parts.find((p) => p.type === t)?.value || 0);
  return new Date(
    pick("year"),
    pick("month") - 1,
    pick("day"),
    pick("hour"),
    pick("minute"),
    pick("second")
  );
}

function fmtPretty(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Maps a preset id → { from, to, label } for the current moment. */
export function resolvePresetDates(preset, customRange = null) {
  const now = nowInShopTz();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(startOfToday); yesterday.setDate(yesterday.getDate() - 1);

  // Helper for "N days back, inclusive of today"
  const daysAgo = (n) => {
    const d = new Date(startOfToday);
    d.setDate(d.getDate() - (n - 1));
    return d;
  };

  // BFCM window: Friday after US Thanksgiving (4th Thursday Nov) → Cyber Monday
  const bfcmRange = (year) => {
    // 4th Thursday of November
    let d = new Date(year, 10, 1); // Nov 1
    let thursdays = 0;
    while (true) {
      if (d.getDay() === 4) {
        thursdays += 1;
        if (thursdays === 4) break;
      }
      d.setDate(d.getDate() + 1);
    }
    const friday = new Date(d); friday.setDate(friday.getDate() + 1);
    const cyberMonday = new Date(friday); cyberMonday.setDate(cyberMonday.getDate() + 3);
    return [friday, cyberMonday];
  };

  switch (preset) {
    case "today":
      return { from: fmtLocal(startOfToday), to: fmtLocal(startOfToday), label: "Today" };
    case "yesterday":
      return { from: fmtLocal(yesterday), to: fmtLocal(yesterday), label: "Yesterday" };

    // "Last N"
    case "last7":
      return { from: fmtLocal(daysAgo(7)), to: fmtLocal(startOfToday), label: "Last 7 days" };
    case "last14":
      return { from: fmtLocal(daysAgo(14)), to: fmtLocal(startOfToday), label: "Last 14 days" };
    case "last30":
      return { from: fmtLocal(daysAgo(30)), to: fmtLocal(startOfToday), label: "Last 30 days" };
    case "last90":
      return { from: fmtLocal(daysAgo(90)), to: fmtLocal(startOfToday), label: "Last 90 days" };
    case "last365":
      return { from: fmtLocal(daysAgo(365)), to: fmtLocal(startOfToday), label: "Last 365 days" };
    case "last_week": {
      // Sun..Sat of the previous calendar week (US-style; Mon..Sun feels off
      // for the Shopify UI which uses Sunday-first)
      const d = new Date(startOfToday);
      const day = d.getDay(); // 0=Sun
      d.setDate(d.getDate() - day - 7); // last Sunday
      const end = new Date(d); end.setDate(end.getDate() + 6);
      return { from: fmtLocal(d), to: fmtLocal(end), label: "Last week" };
    }
    case "last_month": {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: fmtLocal(first), to: fmtLocal(last), label: "Last month" };
    }
    // "This month" — the design picker's row. 1st of the current month .. today, exactly like the
    // dashboard's shared picker so the two repos' labels mean the same window.
    case "this_month":
      return {
        from: fmtLocal(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: fmtLocal(startOfToday),
        label: "This month",
      };
    case "last_quarter": {
      const curQ = Math.floor(now.getMonth() / 3);
      let year = now.getFullYear();
      let q = curQ - 1;
      if (q < 0) { q = 3; year -= 1; }
      const startMonth = q * 3;
      const first = new Date(year, startMonth, 1);
      const last = new Date(year, startMonth + 3, 0);
      return { from: fmtLocal(first), to: fmtLocal(last), label: "Last quarter" };
    }
    case "last_12_months": {
      const start = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
      return { from: fmtLocal(start), to: fmtLocal(startOfToday), label: "Last 12 months" };
    }
    case "last_year": {
      const y = now.getFullYear() - 1;
      return { from: `${y}-01-01`, to: `${y}-12-31`, label: "Last year" };
    }

    // Period to date
    case "wtd": {
      const d = new Date(startOfToday);
      d.setDate(d.getDate() - d.getDay()); // Sunday start
      return { from: fmtLocal(d), to: fmtLocal(startOfToday), label: "Week to date" };
    }
    case "mtd":
      return {
        from: fmtLocal(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: fmtLocal(startOfToday),
        label: "Month to date",
      };
    case "qtd": {
      const startMonth = Math.floor(now.getMonth() / 3) * 3;
      return {
        from: fmtLocal(new Date(now.getFullYear(), startMonth, 1)),
        to: fmtLocal(startOfToday),
        label: "Quarter to date",
      };
    }
    case "ytd":
      return {
        from: fmtLocal(new Date(now.getFullYear(), 0, 1)),
        to: fmtLocal(startOfToday),
        label: "Year to date",
      };

    // BFCM
    case "bfcm_2025":
    case "bfcm_2024":
    case "bfcm_2023":
    case "bfcm_2022": {
      const year = parseInt(preset.slice(-4), 10);
      const [from, to] = bfcmRange(year);
      return { from: fmtLocal(from), to: fmtLocal(to), label: `BFCM ${year}` };
    }

    // Quarters — see the qN_YYYY branch in `default` below. They used to be
    // sixteen hardcoded cases (2023–2026) while the picker GENERATES its quarter
    // ids from the current quarter, so from Jan 2027 "Q1 2027" would have fallen
    // through to the default and silently resolved to Today.

    case "lifetime":
      return { from: "2023-01-01", to: fmtLocal(startOfToday), label: "Lifetime" };

    case "custom": {
      const from = customRange?.from || fmtLocal(startOfToday);
      const to = customRange?.to || fmtLocal(startOfToday);
      return { from, to, label: `${fmtPretty(from)} – ${fmtPretty(to)}` };
    }

    default: {
      // Any calendar quarter, computed from the id instead of enumerated, so the
      // picker's rolling 8-quarter menu can never outrun this switch.
      const q = /^q([1-4])_(\d{4})$/.exec(String(preset || ""));
      if (q) {
        const qi = Number(q[1]);
        const year = Number(q[2]);
        const startMonth = (qi - 1) * 3;
        const first = new Date(year, startMonth, 1);
        const last = new Date(year, startMonth + 3, 0);
        // CLAMPED AT TODAY, exactly like mtd/qtd/ytd/lifetime above and exactly
        // like the calendar grid, which refuses any cell where `d > today`.
        // buildQuartersMenu puts the CURRENT quarter first, so the very first
        // row of the Quarters menu used to resolve to a window ending on the
        // quarter's last calendar day — up to 91 days in the future. The
        // compare twin is n days of ELAPSED history against that length
        // (_prev_range: n = (to − from) + 1), so 38 lived days were measured
        // against 92 full ones and every delta chip printed a ~59% collapse
        // that never happened, while the hero flatlined at $0 over the unlived
        // tail. Dropping the future days downstream is right and stays; the
        // window itself must never claim them.
        //
        // A quarter that has not STARTED is left alone: clamping it would
        // invert the range (from > to). The menu cannot offer one — it walks
        // backwards from the current quarter — but a saved blob or a hand-typed
        // id can, and an empty future window is a truthful answer where an
        // inverted one is not.
        const started = first <= startOfToday;
        const clamped = started && last > startOfToday;
        return {
          from: fmtLocal(first),
          to: fmtLocal(clamped ? startOfToday : last),
          // The label has to move with the window. "Q3 2026" over 38 elapsed
          // days is the same lie the dates were telling.
          label: clamped ? `Q${qi} ${year} to date` : `Q${qi} ${year}`,
        };
      }
      // Treat as today — but say so. An unknown id answering "Today" in silence
      // is how a window nobody asked for ships looking like a window somebody did.
      if (preset && preset !== "today") {
        // eslint-disable-next-line no-console
        console.warn(`[DateRange] unknown preset "${preset}" — falling back to Today`);
      }
      return { from: fmtLocal(startOfToday), to: fmtLocal(startOfToday), label: "Today" };
    }
  }
}

/** How often a relative preset is re-resolved against the reporting-zone wall-clock. */
export const PRESET_RERESOLVE_MS = 5 * 60 * 1000;

/**
 * Keep a resolved window in step with the clock.
 *
 * THE WINDOW IS NOT A MOUNT-TIME CONSTANT. "Today", "Last 7 days" and every
 * other relative preset mean something different after reporting-zone midnight, and
 * this account runs these tabs open all day. A screen that resolves its preset
 * once in a `useState` initialiser keeps serving yesterday for as long as the
 * tab stays open — and worse, keeps POLLING yesterday, so a page whose own
 * server-side blocks are day-anchored (the funnels dashboard band) renders the
 * new day's table beside tiles for the old one.
 *
 * The provider below uses this, and so do the screens that legitimately own a
 * private window (the funnels Dashboard, the Funnels list). One implementation,
 * so those screens cannot drift from the shared one again.
 *
 * `custom` is never re-resolved: a hand-drawn window is two fixed dates and
 * moving it would be the opposite bug.
 *
 * @param {{preset?: string, from?: string, to?: string}} range  current window
 * @param {(next: {preset: string, from: string, to: string, label: string}) => void} apply
 *        called ONLY when the resolved dates actually changed
 */
export function usePresetReresolve(range, apply, intervalMs = PRESET_RERESOLVE_MS) {
  const preset = range && range.preset;
  const from = range && range.from;
  const to = range && range.to;
  useEffect(() => {
    if (!preset || preset === "custom") return undefined;
    const id = setInterval(() => {
      const fresh = resolvePresetDates(preset);
      if (fresh.from !== from || fresh.to !== to) apply({ preset, ...fresh });
    }, intervalMs);
    return () => clearInterval(id);
  }, [preset, from, to, apply, intervalMs]);
}

const DateRangeContext = createContext(null);

export function DateRangeProvider({ children }) {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Re-resolve to refresh from/to for relative presets like "today"
        if (parsed?.preset && parsed.preset !== "custom") {
          return { preset: parsed.preset, ...resolvePresetDates(parsed.preset) };
        }
        if (parsed?.preset === "custom" && parsed.from && parsed.to) {
          return { preset: "custom", ...resolvePresetDates("custom", { from: parsed.from, to: parsed.to }) };
        }
      }
    } catch {
      // ignore
    }
    return { preset: "today", ...resolvePresetDates("today") };
  });

  const apply = useCallback((preset, customRange = null) => {
    const resolved = resolvePresetDates(preset, customRange);
    const next = { preset, ...resolved };
    setState(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        preset,
        ...(preset === "custom" ? { from: resolved.from, to: resolved.to } : {}),
      }));
    } catch {
      // ignore
    }
  }, []);

  // Re-resolve every 5 min for "today"-relative presets to keep them
  // accurate when the dashboard sits open across a day boundary. Shared with
  // the screens that own a private window — see usePresetReresolve.
  usePresetReresolve(state, setState);

  const value = useMemo(() => ({ ...state, apply }), [state, apply]);
  return (
    <DateRangeContext.Provider value={value}>{children}</DateRangeContext.Provider>
  );
}

export function useDateRange() {
  const ctx = useContext(DateRangeContext);
  if (!ctx) {
    // Fallback: caller is outside the provider; return a today-only,
    // read-only shape so they don't crash.
    return { preset: "today", ...resolvePresetDates("today"), apply: () => {} };
  }
  return ctx;
}

// nowInShopTz + SHOP_TZ are exported so the PICKER can share the same clock as
// the resolver. They used to diverge: presets resolved in the reporting zone here
// while the calendar grid highlighted "today" from the browser's own timezone,
// so an operator on any other zone saw a today-cell that disagreed with the
// range the picker actually applied.
export { fmtLocal, fmtPretty, nowInShopTz, SHOP_TZ };
